<template>
  <transition name="s-notification-fade">
    <div
      v-show="visible"
      :id="id"
      :class="['s-notification', horizontalClass, `s-notification-${typeClass}`, loading ? 'is-loading' : '']"
      :data-qa="`notification${dataQa ? '-'+dataQa : ''}`"
      role="alert"
      :style="positionStyle"
      @click.stop="close"
      @mouseenter="timerPause = true"
      @mouseleave="timerPause = false">
      <div class="s-notification__group">
        <h2
          class="s-notification__title"
          :data-qa="`notification-${dataQa ? dataQa+'-' : ''}title`"
          v-text="title"/>
        <div
          v-show="message"
          class="s-notification__content"
          :data-qa="`notification-${dataQa ? dataQa+'-' : ''}content`">
          <slot>
            <p
              v-if="!dangerouslyUseHTMLString"
              :class="{'content-message': loading}">
              {{ message }}
            </p>
            <p
              v-else
              v-dompurify-html="message"
              :class="{'content-message': loading}"/>
          </slot>
        </div>
        <div
          v-if="showClose"
          class="s-notification__closeBtn"
          :class="{'opacity-6': onClick}"
          :data-qa="`notification-${dataQa ? dataQa+'-' : ''}close`"
          @click.stop="onCloseBtnClick ? onCloseBtnClick() : close()">
          {{ closeText || $t('close') }}
        </div>
        <div
          v-if="onClick"
          class="s-notification__clickBtn"
          :class="{'ml-2': showClose}"
          :data-qa="`notification-${dataQa ? dataQa+'-' : ''}click`"
          @click.stop="onClick()">
          {{ clickText || $t('close') }}
        </div>
      </div>
      <div
        v-if="progress || loading"
        :class="`s-notification__progress s-notification__progress__${typeClass}`"
        :data-qa="`notification-${dataQa ? dataQa+'-' : ''}loading`"
        :percent="progressPercent"
        :style="loading ? 'width: 100%;' : 'width: ' + progressPercent + '%;'">
        <div
          v-if="loading"
          class="s-notification__progress-indeterminate"/>
      </div>
    </div>
  </transition>
</template>

<script>
import Vue from 'vue'
import { i18n } from '../utils/i18n.js'

Vue.use(i18n)

let typeMap = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error'
}

/**
 * Всплывающее сообщение
 * @displayName Notification
*/
/*
 * Следующие свойство можно передавать при старте

 * @vue-data {String, Number} id - Не обязательный параметр. Уникальный ключ. Будет присвоено сообщению, если такого ключа еще нет
 * @vue-data {String} title - Заголовок
 * @vue-data {String} message - Текст сообщения
 * @vue-data {Boolean} dangerouslyUseHTMLString - обрабатывается ли сообщение как строка HTML
 * @vue-data {String} type - тип уведомления 'success', 'warning', 'info', 'error'
 * @vue-data {Number} duration - продолжительность до закрытия. Не закроется автоматически, если установлено 0
 * @vue-data {String} position - позиция 'top-right', 'bottom-right', 'bottom-left', 'top-left'
 * @vue-data {Boolean} showClose - показывать ли кнопку закрытия
 * @vue-data {String} closeText - текст кнопки закрытия
 * @vue-data {String} clickText - текст кнопки действия
 * @vue-data {Function} onOpen - функция обратного вызова при открытии. Возвращает ключ сообщения `onOpen: (val) => {this.$log(val)}`
 * @vue-data {Function} onClose - функция обратного вызова при закрытии
 * @vue-data {Function} onClick - функция обратного вызова при нажатии на уведомление
 * @vue-data {Function} onCloseBtnClick - функция на кнопку закрыть
 * @vue-data {Number} verticalOffset - смещение от верхнего края экрана. Каждый экземпляр уведомления в один и тот же момент должен иметь одинаковое смещение
 * @vue-data {Boolean} progress - отображать ли индикатор времени до закрытия
 * @vue-data {Boolean} loading - отображать бесконечный индикатор (duration будет игнорироватся)
 *
 * пример вызова
 * this.$notice.success({
      title: 'Info',
      dangerouslyUseHTMLString: true,
      message: '<strong>This is <i>HTML</i> string</strong>',
      showClose: false,
      position: 'bottom-left',
      type: 'warning'
    });
 *
*/
export default {
  name: 'sNotification',
  componentName: 'sNotification',
  i18n: {
    messages: {
      uk: {
        close: 'Закрити'
      },
      ru: {
        close: 'Закрыть'
      },
      en: {
        close: 'Close'
      },
    }
  },
  data() {
    this.$i18n.locale = sessionStorage.language
    return {
      title: '',
      message: '',
      dangerouslyUseHTMLString: false,
      type: '',
      duration: 3,
      loading: false,
      position: 'top-right',
      showClose: true,
      onClose: null,
      onOpen: null,
      onClick: null,
      onCloseBtnClick: null,
      verticalOffset: 0,
      progress: true,
      closeText: null,
      clickText: null,
      dataQa: '',

      visible: false,
      closed: false,
      removeInInstances: null,
      closeTimer: null,
      timerPause: false,
      progressPercent: 0,
      currentDuration: 0,
    }
  },
  computed: {
    typeClass() {
      return this.type && typeMap[this.type] ? typeMap[this.type] : ''
    },
    horizontalClass() {
      return this.position.indexOf('right') > -1 ? 'right' : 'left'
    },
    verticalProperty() {
      return /^top-/.test(this.position) ? 'top' : 'bottom'
    },
    positionStyle() {
      return {
        [this.verticalProperty]: `${ this.verticalOffset }px`
      }
    }
  },
  watch: {
    closed(newVal) {
      if (newVal) {
        this.visible = false
        this.$el.addEventListener('transitionend', this.destroyElement)
      }
    }
  },
  methods: {
    destroyElement() {
      this.$el.removeEventListener('transitionend', this.destroyElement)
      this.$destroy(true)
      this.$el?.parentNode?.removeChild(this.$el)
    },
    click() {
      if (typeof this.onClick === 'function') {
        this.onClick()
      } else {
        this.close()
      }
    },
    /**
     * Закрываем сообщение
     */
    close() {
      this.clearCloseTimer()
      this.closed = true
      if (typeof this.onClose === 'function') {
        this.onClose()
      }
      this.removeInInstances()
    },
    /**
     * Останавливаем таймер и убираем прогресс
     */
    clearCloseTimer() {
      clearInterval(this.closeTimer)
      if (this.closeTimer) {
        this.currentDuration = 0
        this.progressPercent = 0
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    /**
     * Запуск таймера закрытия
     */
    startTimer() {
      if (this.duration !== 0 && !this.loading) {
        this.currentDuration = (this.duration - 1) * 1000
        this.closeTimer = setInterval(() => {
          if (this.timerPause) { return }
          this.currentDuration -= 10
          this.progressPercent = (this.currentDuration * 100) / (this.duration * 1000)
          if (this.currentDuration <= 20) { this.close() }
        }, 10)
      }
    },
    /**
     * Если Delete || Backspase удаляем таймер
     * если Esc закрываем сообщение/сообщения
     */
    keydown(e) {
      if (e.keyCode === 46 || e.keyCode === 8) {  // Delete || Backspase
        this.clearCloseTimer()
      } else if (e.keyCode === 27) {  // Esc
        if (!this.closed) {
          this.close()
        }
      }
    }
  },
  mounted() {
    if (typeof this.onOpen === 'function') {
      this.onOpen(this.id)
    }
    this.clearCloseTimer()
    this.startTimer()

    document.addEventListener('keydown', this.keydown)
  },
  beforeDestroy() {
    this.clearCloseTimer()
    document.removeEventListener('keydown', this.keydown)
  }
}
</script>
